import React, { createContext, ReactElement, useContext, useReducer } from 'react';

import Widget from 'domain/entity/widget';

export enum ACTION_TYPE {
    SET_WIDGETS = 'SET_WIDGETS',
    RESET_WIDGETS = 'RESET_WIDGETS',
    TOGGLE_RELOAD_WIDGETS = 'TOGGLE_RELOAD_WIDGETS',
}

export const initialState = {
    widgets: [],
    reloadingWidgets: false,
};

type SetWidgets = {
    type: ACTION_TYPE.SET_WIDGETS;
    payload: Widgets;
};

type ResetWidgets = {
    type: ACTION_TYPE.RESET_WIDGETS;
    payload: null;
};

type ReloadWidgets = {
    type: ACTION_TYPE.TOGGLE_RELOAD_WIDGETS;
    payload: boolean;
};

export type WidgetsViewState = {
    widgets: Widgets;
    reloadingWidgets: boolean;
};

export type WidgetsViewDispatch = {
    setWidgets: (widgets: Widgets) => void;
    resetWidgets: () => void;
    toggleReloadWidgets: (value: boolean) => void;
};

type WidgetsViewActionType = SetWidgets | ResetWidgets | ReloadWidgets;
export const useCaseWidgetsViewReducer = (
    state: WidgetsViewState,
    action: WidgetsViewActionType
    // eslint-disable-next-line consistent-return
): WidgetsViewState => {
    const { type, payload } = action;
    // eslint-disable-next-line default-case
    switch (type) {
        case ACTION_TYPE.SET_WIDGETS: {
            return {
                ...state,
                widgets: payload as Widgets,
            };
        }
        case ACTION_TYPE.RESET_WIDGETS: {
            return initialState;
        }
        case ACTION_TYPE.TOGGLE_RELOAD_WIDGETS: {
            return { ...state, reloadingWidgets: payload as boolean };
        }
    }
};

export type Widgets = Widget[];

type WidgetsProviderProps = {
    children: ReactElement;
};

export const WidgetsViewDispatchContext = createContext({} as WidgetsViewDispatch);
export const WidgetsViewStateContext = createContext({} as WidgetsViewState);

export const dispatchWidgetsView = (): WidgetsViewDispatch => useContext(WidgetsViewDispatchContext);
export const useWidgetsView = (): WidgetsViewState => useContext(WidgetsViewStateContext);

export const WidgetsViewProvider = (props: WidgetsProviderProps): ReactElement => {
    const { children } = props;
    const [state, dispatch] = useReducer(useCaseWidgetsViewReducer, initialState);

    const setWidgets = (widgets: Widgets) =>
        dispatch({
            type: ACTION_TYPE.SET_WIDGETS,
            payload: widgets,
        });

    const resetWidgets = () =>
        dispatch({
            type: ACTION_TYPE.RESET_WIDGETS,
            payload: null,
        });

    const toggleReloadWidgets = (value: boolean) =>
        dispatch({
            type: ACTION_TYPE.TOGGLE_RELOAD_WIDGETS,
            payload: value,
        });

    return (
        <WidgetsViewStateContext.Provider value={state}>
            <WidgetsViewDispatchContext.Provider value={{ setWidgets, resetWidgets, toggleReloadWidgets }}>
                {children}
            </WidgetsViewDispatchContext.Provider>
        </WidgetsViewStateContext.Provider>
    );
};
